import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { formatMoney } from '@/utils/money-util'
import styles from './PaymentDistributionCard.module.css'
import { Dropdown, DropdownTitle, DropdownItem } from '@/components/Dropdown'
import { dateFormatters } from '@/utils/date-util'
import { Tooltip } from '@/components/Tooltip'

interface PaymentDistributionItemProps {
  label: string
  value: string
  testId?: string
  button?: ReactNode
}

function PaymentDistributionItem({
  label,
  value,
  testId,
  button,
}: PaymentDistributionItemProps) {
  // After this threshold, text gets an ellipsis style: ...
  // with a tooltip.
  const maxDisplayLength = 20

  return (
    <div className={styles.paymentDistributionItem}>
      <div className={styles.paymentDistributionItemLabel}>{label}</div>
      <div className={styles.paymentDistributionItemValueContainer}>
        <div className={styles.paymentDistributionItemValue} data-test={testId}>
          {value.length <= maxDisplayLength ? (
            <span>{value}</span>
          ) : (
            <Tooltip placement="top" content={value}>
              <span>{value}</span>
            </Tooltip>
          )}
        </div>
        <div className={styles.paymentDistributionItemButton}>{button}</div>
      </div>
    </div>
  )
}

interface CouponRemovalButtonProps {
  onPress: () => unknown
}

function CouponRemovalButton({ onPress }: CouponRemovalButtonProps) {
  return (
    <Button
      label="Remove"
      variant="text"
      size="baseline"
      testId="job-coupon-remove"
      onPress={onPress}
    />
  )
}

interface PaymentDistributionButtonProps {
  onPress: () => unknown
}

function PaymentDistributionButton({
  onPress,
}: PaymentDistributionButtonProps) {
  return (
    <Button
      label="Edit split"
      variant="text"
      size="baseline"
      testId="job-pro-cut-amount-edit-split"
      onPress={onPress}
    />
  )
}

export interface PaymentDistributionReceipt {
  id?: string
  receiptUrl: string
  amount: number
  dateCreated: Date
}

interface PaymentDistributionDropdownProps {
  receipts: PaymentDistributionReceipt[]
}

function PaymentDistributionDropdown({
  receipts,
}: PaymentDistributionDropdownProps) {
  return receipts.length === 1 ? (
    <Button
      label="View receipt"
      variant="text"
      size="baseline"
      testId="job-client-paid-amount-view-receipts"
      href={receipts[0].receiptUrl}
      target="_blank"
    />
  ) : (
    <Dropdown
      autoDismiss
      position="top-end"
      button={
        <Button
          label="View receipts"
          variant="text"
          size="baseline"
          testId="job-client-paid-amount-view-receipts"
        />
      }
    >
      <DropdownTitle>View receipts</DropdownTitle>
      {receipts.map((t) => (
        <DropdownItem key={t.id}>
          <a
            className={styles.viewReceiptsDropdownItem}
            href={t.receiptUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {formatMoney(t.amount / 100)} on{' '}
            {dateFormatters.dateTimeDefault.format(t.dateCreated)}
          </a>
        </DropdownItem>
      ))}
    </Dropdown>
  )
}

export interface PaymentDistributionCardProps {
  primaryAmount: number
  canViewCoupon?: boolean
  couponDisplay: string
  couponCode?: string
  canRemoveCoupon?: boolean
  onRemoveCoupon: () => unknown
  canViewProCut: boolean
  providerCutAmount: number
  canSplitPayout: boolean
  onEditSplit: () => unknown
  total: number
  paidAmount: number
  receipts: PaymentDistributionReceipt[]
  refundedAmount: number
}

export function PaymentDistributionCard({
  primaryAmount,
  canViewCoupon,
  couponDisplay,
  couponCode,
  canRemoveCoupon,
  onRemoveCoupon,
  canViewProCut,
  providerCutAmount,
  canSplitPayout,
  onEditSplit,
  total,
  paidAmount,
  receipts,
  refundedAmount,
}: PaymentDistributionCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment distribution</CardTitle>
      </CardHeader>
      <CardContent scroll>
        <PaymentDistributionItem
          label="Primary amount"
          value={formatMoney(primaryAmount)}
          testId="job-primary-amount"
        />
        {canViewCoupon && (
          <PaymentDistributionItem
            label="Coupon"
            value={couponDisplay}
            testId={`job-applied-coupon-${couponCode}`}
            button={
              canRemoveCoupon ? (
                <CouponRemovalButton onPress={onRemoveCoupon} />
              ) : null
            }
          />
        )}
        {canViewProCut && (
          <PaymentDistributionItem
            label="Total Pro cut"
            value={formatMoney(providerCutAmount)}
            testId="job-pro-cut-amount"
            button={
              canSplitPayout ? (
                <PaymentDistributionButton onPress={onEditSplit} />
              ) : null
            }
          />
        )}
        <PaymentDistributionItem
          label="Total"
          value={formatMoney(total)}
          testId="job-total"
        />
        <PaymentDistributionItem
          label="Client paid"
          value={formatMoney(paidAmount)}
          testId="job-client-paid-amount"
          button={
            receipts.length > 0 &&
            paidAmount > 0 && (
              <PaymentDistributionDropdown receipts={receipts} />
            )
          }
        />

        {refundedAmount > 0 && (
          <PaymentDistributionItem
            label="Refunded"
            value={formatMoney(refundedAmount)}
            testId="job-client-refunded-amount"
          />
        )}
      </CardContent>
    </Card>
  )
}
